/**
 * Finds a report within a nested array structure by its PBIREPORTID.
 *
 * @param {Array} reportsArray - The array of report groups, e.g., [['Group 1', [reports...]], ...].
 * @param {string} id - The PBIREPORTID to search for.
 * @returns {Object|null} The found report object or null if not found.
 */
export const findReportById = (reportsArray, id) => {
  // Return null if no ID is provided to search for.
  if (!id) return null;

  for (const [groupName, reports] of reportsArray) {
    // Find the report where the PBIREPORTID matches the given id.
    const found = reports.find((report) => report.PBIREPORTID === id);
    if (found) return found;
  }

  // Return null if no report is found across all groups.
  return null;
};

/**
 * Creates a URL-friendly slug from a string (e.g., "Financial Performance" -> "financial-performance")
 * * @param {string} text - The string to convert
 * @returns {string} The URL-safe slugified string
 */
export const generateSlug = (text) => {
  if (!text) return '';
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-') // Replace spaces with -
    .replace(/[^\w-]+/g, '') // Remove all non-word chars
    .replace(/--+/g, '-'); // Replace multiple - with single -
};

/**
 * Finds a report within a nested array structure by matching URL slugs back to the original group and report names.
 *
 * @param {Array} reportsArray - The array of report groups.
 * @param {string} groupSlug - The URL slug for the group name (e.g., from useParams).
 * @param {string} reportSlug - The URL slug for the report name (e.g., from useParams).
 * @returns {Object|null} The found report object or null if not found.
 */
export const findReportByName = (reportsArray, groupSlug, reportSlug) => {
  // Return null if any required parameters are missing
  if (!reportsArray || !groupSlug || !reportSlug) return null;

  for (const [groupName, groupReports] of reportsArray) {
    // Check if the current group's slug matches the URL group slug
    if (generateSlug(groupName) === groupSlug) {
      // Find the report inside this group where the report slug matches
      const foundReport = groupReports.find(
        (report) => generateSlug(report.ReportName) === reportSlug,
      );

      if (foundReport) return foundReport;
    }
  }

  // Return null if no match is found
  return null;
};
